import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { fetchDataWithAutoRetry } from '../../utils/fetchDataWithAutoRetry'
import { bybitApiEndpoint } from '../constants'
import { commonResponseSchema, successReponseSchema } from '../schemas'
import { createPrivateGetHeaders } from '../utils'
import { type ClosedPnl, resultSchema } from './schema'

const responseSchema = successReponseSchema.extend({
  result: resultSchema,
})

const urlPath = '/v5/position/closed-pnl'

type FetchClosedPlHistoriesParameters = {
  apiKey: string
  apiSecret: string
  category: 'linear' | 'inverse'
  from: number
  to: number
  limit: number
  proxy?: string
  headers?: Record<string, string>
}

/**
 * - Query user's closed profit and loss records
 * - UTA2.0, UTA1.0(except inverse): support getting the past 730 days historical data
 * - the rule is endTime - startTime <= 7 days
 * - Limit for data size per page. [1, 100]
 */
export const fetchClosedPlHistories = ({
  apiKey,
  apiSecret,
  category,
  from,
  to,
  limit,
  proxy,
  headers,
}: FetchClosedPlHistoriesParameters): Promise<readonly ClosedPnl[]> =>
  fetchDataWithAutoRetry({
    from,
    to,
    limit,
    desc: true,
    fetchData: async ({ from, to, limit, desc }) => {
      if (!desc) {
        throw new Error('desc must be true')
      }
      if (limit > 100) {
        throw new Error('limit must be less than or equal to 100')
      }

      const url = new URL(urlPath, bybitApiEndpoint)
      url.searchParams.append('category', category)
      url.searchParams.append('limit', limit.toString())
      url.searchParams.append('startTime', from.toString())
      url.searchParams.append('endTime', to.toString())
      const query = url.searchParams.toString()

      const response = await rest(
        proxy === undefined
          ? url.toString()
          : `${proxy}${encodeURIComponent(url.toString())}`,
        {
          fetchOptions: {
            method: 'GET',
            headers: {
              ...headers,
              ...(await createPrivateGetHeaders({
                apiKey,
                apiSecret,
                query: query.length > 0 ? query : '',
              })),
            },
          },
          schema: z.union([responseSchema, commonResponseSchema]),
        },
      )

      if (!('result' in response)) {
        throw new Error(response.retMsg)
      }
      return response.result.list
    },
  })
